/**
 * Static mappings of Qore dispatch statusId to status value
 * and Qore dispatch stateId to state value.
 * These values are already found in the redux store
 * under the store.types.dispatchStates and store.types.dispatchStatuses (as arrays)
 * fields; this file is to facilitate conditional logic and use cases where
 * we don't want to iterate through the array to find the correct state or status value
 */

export const QORE_DISPATCH_STATE_ID_TO_STATE_VALUE = {
  1: 'Provider on the way',
  2: 'Provider on site',
  3: 'Provider at Drop off',
  4: 'Provider En-route to Drop off',
  5: 'Provider Completed',
  6: 'Provider Accepted',
  7: 'Dispatch created',
  8: 'Driver Assigned',
  9: 'Provider Cancelled',
  10: 'Customer Cancelled',
  11: 'Customer or vehicle not at Pick Up',
  12: 'Provider Cancelled - DO NOT RE-DISPATCH',
  13: 'Provider Redispatch Accepted',
  14: 'Searching For Providers',
  15: 'Processing Auto Dispatch',
  16: 'Awaiting Manual Dispatch',
  17: 'Scheduled',
  18: 'Retrying Auto Dispatch Initialization',
};

export const QORE_DISPATCH_STATE_VALUE_TO_ID = {
  'Provider on the way': 1,
  'Provider on site': 2,
  'Provider at Drop off': 3,
  'Provider En-route to Drop off': 4,
  'Provider Completed': 5,
  'Provider Accepted': 6,
  'Dispatch created': 7,
  'Driver Assigned': 8,
  'Provider Cancelled': 9,
  'Customer Cancelled': 10,
  'Customer or vehicle not at Pick Up': 11,
  'Provider Cancelled - DO NOT RE-DISPATCH': 12,
  'Provider Redispatch Accepted': 13,
  'Searching For Providers': 14,
  'Processing Auto Dispatch': 15,
  'Awaiting Manual Dispatch': 16,
  Scheduled: 17,
  'Retrying Auto Dispatch Initialization': 18,
};

export const QORE_DISPATCH_STATUS_ID_TO_STATUS_VALUE = {
  1: 'Accepted',
  2: 'In Route',
  3: 'On Scene',
  4: 'Tow In Progress',
  5: 'Destination Arrival',
  6: 'Complete',
  7: 'New',
  8: 'Cancelled',
  9: 'GOA',
  10: 'Cancelled RR',
};

export const QORE_DISPATCH_STATUS_VALUE_TO_ID = {
  Accepted: 1,
  'In Route': 2,
  'On Scene': 3,
  'Tow In Progress': 4,
  'Destination Arrival': 5,
  Complete: 6,
  New: 7,
  Cancelled: 8,
  GOA: 9,
  'Cancelled RR': 10,
};

export const QORE_DISPATCH_STATUS_AND_STATE_MAPPING = {
  Accepted: {
    status: 'Accepted',
    statusId: QORE_DISPATCH_STATUS_VALUE_TO_ID.Accepted,
    state: 'Provider Accepted',
    stateId: QORE_DISPATCH_STATE_VALUE_TO_ID['Provider Accepted']
  },
  'In Route': {
    status: 'In Route',
    statusId: QORE_DISPATCH_STATUS_VALUE_TO_ID['In Route'],
    state: 'Provider on the way',
    stateId: QORE_DISPATCH_STATE_VALUE_TO_ID['Provider on the way']
  },
  GOA: {
    status: 'GOA',
    statusId: QORE_DISPATCH_STATUS_VALUE_TO_ID.GOA,
    state: 'Customer or vehicle not at Pick Up',
    stateId: QORE_DISPATCH_STATE_VALUE_TO_ID['Customer or vehicle not at Pick Up']
  },
  'On Scene': {
    status: 'On Scene',
    statusId: QORE_DISPATCH_STATUS_VALUE_TO_ID['On Scene'],
    state: 'Provider on site',
    stateId: QORE_DISPATCH_STATE_VALUE_TO_ID['Provider on site']
  },
  New: {
    status: 'New',
    statusId: QORE_DISPATCH_STATUS_VALUE_TO_ID.New,
    state: 'Dispatch Created',
    stateId: QORE_DISPATCH_STATE_VALUE_TO_ID['Dispatch created']
  },
  'Tow In Progress': {
    status: 'Tow In Progress',
    statusId: QORE_DISPATCH_STATUS_VALUE_TO_ID['Tow In Progress'],
    state: 'Provider En-route to Drop off',
    stateId: QORE_DISPATCH_STATE_VALUE_TO_ID['Provider En-route to Drop off']
  },
  Incomplete: {
    status: 'New',
    statusId: QORE_DISPATCH_STATUS_VALUE_TO_ID.New,
    state: 'Dispatch Created',
    stateId: QORE_DISPATCH_STATE_VALUE_TO_ID['Dispatch created']
  },
  'Destination Arrival': {
    status: 'Destination Arrival',
    statusId: QORE_DISPATCH_STATUS_VALUE_TO_ID['Destination Arrival'],
    state: 'Provider at Drop off',
    stateId: QORE_DISPATCH_STATE_VALUE_TO_ID['Provider at Drop off']
  },
  Complete: {
    status: 'Complete',
    statusId: QORE_DISPATCH_STATUS_VALUE_TO_ID.Complete,
    state: 'Provider Completed',
    stateId: QORE_DISPATCH_STATE_VALUE_TO_ID['Provider Completed']
  },
  Cancelled: {
    status: 'Cancelled',
    statusId: QORE_DISPATCH_STATUS_VALUE_TO_ID.Cancelled,
    state: 'Provider Cancelled',
    stateId: QORE_DISPATCH_STATE_VALUE_TO_ID['Provider Cancelled']
  },
};

/**
 * All State IDs that deal with cancel logic
 */
export const QORE_DISPATCH_CANCEL_STATE_IDS = {
  9: 'Provider Cancelled',
  10: 'Customer Cancelled',
  11: 'Customer or vehicle not at Pick Up',
  12: 'Provider Cancelled - DO NOT RE-DISPATCH'
};

/**
 * All Status IDs that deal with cancel logic
 */
export const QORE_DISPATCH_CANCEL_STATUS_IDS = {
  8: 'Cancelled',
  9: 'GOA',
  10: 'Cancelled RR'
};

/**
 * All State IDs that do not allow a dispatch to be canceled
 */
export const QORE_DISPATCH_CANNOT_BE_CANCELED_STATE_IDS = {
  10: 'Customer Cancelled',
  12: 'Provider Cancelled - DO NOT RE-DISPATCH'
};

/**
 * All values for dispatch states.
 */
export const QORE_DISPATCH_STATE_VALUES = {
  providerOnTheWay: 'Provider on the way',
  providerOnSite: 'Provider on site',
  providerAtDropOff: 'Provider at Drop off',
  providerEnrouteToDropOff: 'Provider En-route to Drop off',
  providerCompleted: 'Provider Completed',
  providerAccepted: 'Provider Accepted',
  dispatchCreated: 'Dispatch created',
  driverAssigned: 'Driver Assigned',
  providerCancelled: 'Provider Cancelled',
  customerCancelled: 'Customer Cancelled',
  customerNotAtPickUp: 'Customer or vehicle not at Pick Up',
  providerCancelledNoReDispatch: 'Provider Cancelled - DO NOT RE-DISPATCH',
  providerRedispatchAccepted: 'Provider Redispatch Accepted',
  searchingForProviders: 'Searching For Providers',
  processingAutoDispatch: 'Processing Auto Dispatch',
  awaitingManualDispatch: 'Awaiting Manual Dispatch',
  scheduled: 'Scheduled',
  retryingAutoDispatchInitialization: 'Retrying Auto Dispatch Initialization',
};

/**
 * Secondary/Impound Status IDs and their descriptions
 */
export const SECONDARY_STATUS_ID_TO_DESCRIPTION = {
  1: 'New',
  2: 'Pending Documentation',
  3: 'Pending Approval',
  4: 'Pending Location',
  5: 'Pending Release',
  6: 'Released',
  7: 'Accepted',
  8: 'Complete',
  9: 'Cancelled'
};

/**
 * Secondary/Impound Status constants for easy reference
 */
export const SECONDARY_STATUS = {
  NEW: 1,
  PENDING_DOCUMENTATION: 2,
  PENDING_APPROVAL: 3,
  PENDING_LOCATION: 4,
  PENDING_RELEASE: 5,
  RELEASED: 6,
  ACCEPTED: 7,
  COMPLETE: 8,
  CANCELLED: 9
};

/**
 * Secondary status description to ID mapping
 */
export const SECONDARY_STATUS_DESCRIPTION_TO_ID = {
  New: 1,
  'Pending Documentation': 2,
  'Pending Approval': 3,
  'Pending Location': 4,
  'Pending Release': 5,
  Released: 6,
  Accepted: 7,
  Complete: 8,
  Cancelled: 9
};
